import api, { handleApiError, fetchAllDataRest } from './client';

export const subscriptionService = {
  // Helper function per normalizzare le date
  normalizeDate: (dateValue, fallbackToToday = true) => {
    if (!dateValue) {
      if (fallbackToToday) {
        return new Date().toISOString();
      }
      throw new Error('Date value is required but not provided');
    }

    if (typeof dateValue === 'string' && dateValue.includes('T')) {
      return dateValue;
    }

    try {
      return new Date(dateValue).toISOString();
    } catch (error) {
      console.error('Error normalizing date:', error, 'value:', dateValue);
      if (fallbackToToday) {
        return new Date().toISOString();
      }
      throw error;
    }
  },

  createSubscription: async (subscriptionData) => {
    try {
      const payload = {
        userId: subscriptionData.userId,
        groupId: subscriptionData.groupId,
        price: subscriptionData.price,
        offerId: subscriptionData.offerId,
        startDate: subscriptionService.normalizeDate(subscriptionData.startDate),
        endDate: subscriptionService.normalizeDate(subscriptionData.endDate),
        notes: subscriptionData.notes,
        usedEntriesAdjustment: subscriptionData.usedEntriesAdjustment,
        totalTickets: subscriptionData.totalTickets != null && subscriptionData.totalTickets !== '' ? parseInt(subscriptionData.totalTickets, 10) : null,
        isTrial: subscriptionData.isTrial !== undefined ? !!subscriptionData.isTrial : null,
        paymentDate: subscriptionData.paymentDate ? subscriptionService.normalizeDate(subscriptionData.paymentDate) : null,
        paymentMethod: subscriptionData.paymentMethod || null,
        hasInstallments: !!subscriptionData.hasInstallments,
        installments: subscriptionData.hasInstallments && subscriptionData.installments
          ? subscriptionData.installments.map((i) => ({
              id: i.id || null,
              installmentNumber: i.installmentNumber,
              amount: parseFloat(i.amount) || 0,
              dueDate: subscriptionService.normalizeDate(i.dueDate),
              paymentDate: i.paymentDate ? subscriptionService.normalizeDate(i.paymentDate) : null,
              notes: i.notes || null,
            }))
          : null,
      };

      const response = await api.post('/api/subscriptions', payload);
      return {
        success: true,
        data: response.data,
        message: 'Iscrizione creata con successo',
      };
    } catch (error) {
      console.error('Create subscription error:', error);
      return handleApiError(error, "Errore nella creazione dell'iscrizione");
    }
  },

  getActivePayPerUseSubscriptions: async (userId, groupId, startDate = null, endDate = null) => {
    try {
      const response = await api.get('/api/subscriptions/active-pay-per-use', {
        params: {
          userId,
          groupId,
          startDate: startDate ? subscriptionService.normalizeDate(startDate) : undefined,
          endDate: endDate ? subscriptionService.normalizeDate(endDate) : undefined,
        },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Get active pay per use subscriptions error:', error);
      return handleApiError(error, 'Errore nel caricamento degli abbonamenti a ingressi');
    }
  },

  updateSubscription: async (subscriptionData) => {
    try {
      const payload = {
        groupId: subscriptionData.groupId,
        price: subscriptionData.price,
        startDate: subscriptionService.normalizeDate(subscriptionData.startDate),
        endDate: subscriptionService.normalizeDate(subscriptionData.endDate),
        offerId: subscriptionData.offerId,
        notes: subscriptionData.notes,
        usedEntriesAdjustment: subscriptionData.usedEntriesAdjustment,
        totalTickets: subscriptionData.totalTickets != null && subscriptionData.totalTickets !== '' ? parseInt(subscriptionData.totalTickets, 10) : null,
        isTrial: subscriptionData.isTrial !== undefined ? !!subscriptionData.isTrial : null,
        paymentDate: subscriptionData.paymentDate ? subscriptionService.normalizeDate(subscriptionData.paymentDate) : null,
        paymentMethod: subscriptionData.paymentMethod || null,
        hasInstallments: subscriptionData.hasInstallments,
        installments: subscriptionData.installments
          ? subscriptionData.installments.map((i) => ({
              id: i.id || null,
              installmentNumber: i.installmentNumber,
              amount: parseFloat(i.amount) || 0,
              dueDate: subscriptionService.normalizeDate(i.dueDate),
              paymentDate: i.paymentDate ? subscriptionService.normalizeDate(i.paymentDate) : null,
              notes: i.notes || null,
            }))
          : null,
      };

      const response = await api.put(`/api/subscriptions/${subscriptionData.id}`, payload);
      return {
        success: true,
        data: response.data,
        message: 'Iscrizione aggiornata con successo',
      };
    } catch (error) {
      console.error('Update subscription error:', error);
      return handleApiError(error, "Errore nell'aggiornamento dell'iscrizione");
    }
  },

  deleteSubscription: async (subscriptionId) => {
    try {
      await api.delete(`/api/subscriptions/${subscriptionId}`);
      return { success: true, message: 'Iscrizione eliminata con successo' };
    } catch (error) {
      console.error('Delete subscription error:', error);
      return handleApiError(error, "Errore nell'eliminazione dell'iscrizione");
    }
  },

  payInstallment: async (installmentId, paymentDate, paymentMethod = null) => {
    try {
      const payload = {
        paymentDate: subscriptionService.normalizeDate(paymentDate),
      };
      if (paymentMethod) {
        payload.paymentMethod = paymentMethod;
      }
      const response = await api.post(`/api/subscriptions/installments/${installmentId}/pay`, payload);

      return {
        success: true,
        data: response.data,
        message: 'Rata pagata con successo',
      };
    } catch (error) {
      console.error('Pay installment error:', error);
      return handleApiError(error, 'Errore nella registrazione del pagamento della rata');
    }
  },

  archiveUserInGroup: async (groupId, userId, isArchived) => {
    try {
      const response = await api.post('/api/subscriptions/archive-user-in-group', {
        groupId,
        userId,
        isArchived,
      });

      return {
        success: true,
        data: response.data?.success ?? true,
        message: isArchived ? 'Atleta archiviato con successo' : 'Atleta ripristinato con successo',
      };
    } catch (error) {
      console.error('Archive user in group error:', error);
      return handleApiError(error, "Errore nell'archiviazione dell'atleta");
    }
  },

  getSubscriptions: async (filters = {}) => {
    try {
      const queryParams = {
        userId: filters.userId || undefined,
        courseId: filters.courseId || undefined,
        groupId: filters.groupId || undefined,
        status: filters.status || undefined,
        page: filters.page || 1,
        limit: filters.limit || 20,
        academicYear: filters.academicYear || undefined,
        excludeTrials: filters.excludeTrials !== undefined ? filters.excludeTrials : undefined,
      };

      if (filters.exportAll) {
        return await fetchAllDataRest('/api/subscriptions', queryParams, 'iscrizioni');
      }

      const response = await api.get('/api/subscriptions', { params: queryParams });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Get subscriptions error:', error);
      return handleApiError(error, 'Errore nel caricamento degli abbonamenti');
    }
  },
};

export default subscriptionService;
